<template lang="pug">
.activity-screenshot
  template(v-if="activity")
    a(
      :href="projectLinkPath"
      target="_blank"
    )
      .label-project-name(
        v-tooltip="{ placement: 'top', html: true }"
        :title="projectLinkTitle"
      )
        .ellipsis.project-names {{ allProjectNames }}
        i.label-project-icon.hi.hi-external.hi-10.ml-5

    a(
      v-if="hasTaskSummaries"
      :href="taskLinkPath"
      target="_blank"
    )
      .task-name(
        v-tooltip="{ placement: 'top', html: true }"
        :title="`Go to ${organizationTaskPlural}`"
      )
        .ellipsis {{ allTaskSummaries }}
        i.task-icon.hi.hi-external.hi-10.ml-5
    .task-name.text-superlight(v-else) No {{ organizationTaskPlural }}
    .top-shot-body
      .shot-body.fs-exclude(v-if="hasPlanLimitedScreens")
        .screenshot-activity-overlay
          .screenshots-limit-achieved-overlay
            p.screenshots-limit-achieved
              i.hi.hi-lock.text-blue
              strong.small.text-white
                | Limit reached
          a.btn.btn-sm.btn-outline-default.slot-view.overlay-content.shot.no-action.screenshots-limit-achieved-link(
            v-if="canUpgradePlan"
            @click.prevent="goToPlansPage"
          )
            | Upgrade
          a.btn.btn-sm.btn-outline-default.slot-view.overlay-content.shot.no-action.screenshots-limit-achieved-link(
            v-else-if="canViewUpgradePlanInfo"
            disabled="disabled"
            title="Contact your Organization owner to upgrade plans"
          )
            | Upgrade
        .state_shot.enforce-limits_blured-data(v-if="shouldShowScreenshots")
          img.no-action(:data-lazy-src="firstScreenshot.thumb_url")

      .shot-body.fs-exclude(
        v-else
        v-track:click="screenshotTrack"
        :class="shotBodyClass"
        @click="showGalleryOrSelect"
      )
        template(v-if="hasScreens")
          ActivitiesBlockActions(
            v-if="!fakeDataRequired"
            :activity="activity"
            :all-project-names="allProjectNames"
          )
            a.btn.btn-sm.btn-outline-default.slot-view.overlay-content.shot.no-action(v-if="shouldShowScreenshots")
              | View screens
          .state_shot(v-if="shouldShowScreenshots")
            img.no-action(:data-lazy-src="firstScreenshot.thumb_url")
          .state_shot.no_screenshot(v-else)
            .state-shot-wrapper
              i.hi.hi-screenshot
              div
                template(v-if="hasScreens && !screensDeleted")
                  | Screenshot hidden
                template(v-else)
                  | Screenshot deleted
        template(v-else)
          ActivitiesBlockActions(
            :activity="activity"
            :all-project-names="allProjectNames"
          )
          .state_shot(:class="noShotCssClass")
            .state-shot-wrapper
              i.hi(:class="noShotIcon")
              div
                p No screenshot
                p(v-if="noScreenshotReason") {{ noScreenshotReason }}
      .screen-count-pill(v-if="hasScreens && !screensDeleted && !hasPlanLimitedScreens")
        | {{ screenCount }} {{ screenCount | pluralize('screen', 'screens') }}
      .meta
        .meta-head
          .meta-interval {{ activity.slot_span }}
          .meta-icons(v-if="!viewOnly")
            ActivitiesNotePopover(
              :own-activity="activity.own_activity"
              :time-slot="activity.time_slot"
            )
              template(slot="trigger")
                a.inline-link.clickable(
                  v-if="ownActivity || hasNotes"
                  :class="{ 'text-success': hasNotes }"
                )
                  i.hi.hi-edit
        .row
          .col-xs-12.activity
            template(v-if="showProgressBar")
              ActivitiesProgressBar(
                v-tooltip="{ html: true, placement: 'bottom' }"
                :activity="activity"
                :title="progressBarTooltip"
              )
            template(v-else)
              .progress.no-data
            | {{ activityLabel }}
        AppGalleryItem.hidden(
          v-for="screenshot in availableScreenshots"
          :key="screenshot.index"
          :url="screenshot.original_url"
          :thumb-url="screenshot.thumb_url"
          :caption="screenshot.caption"
        )
  template(v-else)
    .label-project-name.invisible
    .task-name.invisible
    .blank No activity
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import ActivitiesBlockActions from './ActivitiesBlockActions.vue';
import ActivitiesProgressBar from './ActivitiesProgressBar.vue';
import { FAKE_DATA_IMAGE_URLS } from '~/rails_assets';
import { PROJECT_TYPES } from '~/app_constants';
import { sample } from 'lodash-es';
import ActivitiesNotePopover from './ActivitiesNotePopover.vue';

export default {
  name: 'ActivitiesBlock',
  components: {
    ActivitiesBlockActions,
    ActivitiesProgressBar,
    ActivitiesNotePopover,
  },
  inject: ['$appGallery'],
  props: {
    activity: {
      type: Object,
      default: () => ({}),
    },
    observer: {
      type: IntersectionObserver,
      required: true,
    },
    viewOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      screenshotTrack: Object.freeze({
        label: 'Open screenshot',
        props: { screenshot_opened: this.activity?.slot_span },
      }),
    };
  },
  computed: {
    ...mapState({
      fakeData: (state) => state.fake_data,
      proxyUser: (state) => state.proxy_user,
      screenshotSettingEnabled: (state) => state.screenshot_setting_enabled,
    }),
    ...mapGetters(['organizationTaskPlural', 'organization']),
    ...mapState({
      selectedTimeSlots: (state) => state.selected_time_slots.map((ts) => ts.timeSlot),
    }),
    projectNames() {
      return this.activity.project_names || [];
    },
    projectTypes() {
      return this.activity.project_types || [];
    },
    workOrder() {
      return this.projectTypes[0] === PROJECT_TYPES.workOrder;
    },
    projectIds() {
      return this.activity.project_ids || [];
    },
    projectLinkPath() {
      if (this.workOrder) {
        return `/organizations/${this.organization.id}/work_orders/${this.projectIds[0]}`;
      }

      return `/projects/${this.projectIds[0]}`;
    },
    projectLinkTitle() {
      if (this.workOrder) {
        return 'Go to work order';
      }

      return 'Go to project';
    },
    taskLinkPath() {
      if (this.workOrder) {
        return this.projectLinkPath;
      }

      return `/organizations/${this.organization.id}/tasks?filters[project]=${this.projectIds[0]}`;
    },
    allProjectNames() {
      return this.projectNames.join(', ');
    },
    taskSummaries() {
      return this.activity?.task_summaries?.split(', ') || [];
    },
    allTaskSummaries() {
      return this.taskSummaries?.join(', ');
    },
    hasTaskSummaries() {
      return (this.taskSummaries?.length ?? 0) > 0;
    },
    hasScreens() {
      return !this.activity.idle_time && typeof this.screenCount === 'number' ? this.screenCount > 0 : false;
    },
    hasPlanLimitedScreens() {
      return this.activity.screens.some((screen) => screen.plan_limited);
    },
    screenshots() {
      return this.activity.screens.filter((screen) => !!screen.thumb_url);
    },
    availableScreenshots() {
      return this.screenshots.filter((screen) => !screen.deleted);
    },
    hasScreenshots() {
      return !this.activity.idle_time && (this.availableScreenshots.length || this.fakeDataRequired);
    },
    firstScreenshot() {
      if (this.fakeDataRequired) {
        return sample(FAKE_DATA_IMAGE_URLS);
      }

      return this.hasScreenshots ? this.screenshots[0] : null;
    },
    shouldShowScreenshots() {
      return this.hasScreenshots && !this.proxyUser && !this.screensDeleted;
    },
    screensDeleted() {
      const screens = this.activity?.screens ?? [];

      return screens.filter((screen) => !screen.deleted).length === 0;
    },
    screenCount() {
      const screens = this.activity?.screens ?? [];

      return screens.length;
    },
    permissions() {
      return this.activity?.permissions;
    },
    canChangeActivityModify() {
      return this.permissions?.can_change_activity_modify;
    },
    canChangeActivityDelete() {
      return this.permissions?.can_change_activity_delete;
    },
    lockedActivity() {
      return Boolean(this.activity?.locked);
    },
    canOverrideLocked() {
      return Boolean(this.activity?.overrideable);
    },
    canSelectActivities() {
      return (
        (this.canChangeActivityDelete ||
          this.canChangeActivityModify ||
          (this.lockedActivity && this.canOverrideLocked)) &&
        (!this.lockedActivity || this.canOverrideLocked)
      );
    },
    canUpgradePlan() {
      return this.activity.upgrade_plan_button_state === 'enabled';
    },
    canViewUpgradePlanInfo() {
      return this.activity.upgrade_plan_button_state === 'disabled';
    },
    canEditTimesheet() {
      return this.permissions?.can_edit_timesheet;
    },
    canDeleteScreens() {
      return this.permissions?.can_delete_screens;
    },
    canDeleteActivity() {
      return this.permissions?.can_delete_activity;
    },
    canModifyActivity() {
      return this.permissions?.can_modify_activity;
    },
    canChangeActivity() {
      return this.permissions?.can_change_activity;
    },
    userTrackingEnabled() {
      return this.activity?.user_tracking_enabled;
    },
    associatedRecords() {
      return this.activity?.associated_records;
    },
    isManualTime() {
      return this.activity.manual_time;
    },
    isDesktopTime() {
      return this.activity.desktop_time;
    },
    isMobileTime() {
      return this.activity.mobile_time;
    },
    isBrowserTime() {
      return this.activity.browser_time;
    },
    isWebTime() {
      return this.activity.web_time;
    },
    isApiTime() {
      return this.activity.api_time;
    },
    isResumedTime() {
      return this.activity.resumed_time;
    },
    isIdleTime() {
      return this.activity.idle_time;
    },
    wantsInput() {
      return this.activity.wants_input;
    },
    supportsInput() {
      return this.activity.supports_input;
    },
    hasClient() {
      return !!this.activity.client;
    },
    ownActivity() {
      return this.activity.own_activity;
    },
    hasNotes() {
      return this.activity.has_notes;
    },
    noScreenshotReason() {
      if (this.isManualTime) {
        return 'Manual entry';
      }
      if (this.isDesktopTime && !this.screenshotSettingEnabled) {
        return 'Screenshot frequency 0';
      }
      if (this.isMobileTime) {
        return 'Mobile app used';
      }
      if (this.isBrowserTime) {
        return 'Browser ext. used';
      }
      if (this.isWebTime) {
        return 'Web timer used';
      }
      if (this.isApiTime) {
        return 'Time added via API';
      }
      if (this.isResumedTime) {
        return 'App catch up';
      }
      if (this.isIdleTime) {
        return 'Idle time';
      }

      return null;
    },
    noShotCssClass() {
      let cssClass = 'no_screenshot';

      if (this.isMobileTime) {
        cssClass = 'mobile_time';
      } else if (this.isBrowserTime || this.isWebTime || this.isApiTime) {
        cssClass = 'browser_time';
      } else if (this.isIdleTime) {
        cssClass = 'idle_time';
      }

      return cssClass;
    },
    noShotIcon() {
      let iconClass = 'hi-screenshot';

      if (this.isManualTime) {
        iconClass = 'hi-manual-time';
      } else if (this.isMobileTime) {
        iconClass = 'hi-mobile-time';
      } else if (this.isBrowserTime || this.isWebTime || this.isApiTime) {
        iconClass = 'hi-browser-time';
      } else if (this.isIdleTime) {
        iconClass = 'hi-idle';
      }

      return iconClass;
    },
    formattedOverall() {
      return this.activity.overall + '%';
    },
    formattedKeyboard() {
      return this.activity.keyboard + '%';
    },
    formattedMouse() {
      return this.activity.mouse + '%';
    },
    activityLabel() {
      const tracked = this.activity.tracked;
      const formattedTime = tracked < 60 ? `${tracked} seconds` : `${Math.floor(tracked / 60)} minutes`;

      if (this.showProgressBar) {
        return `${this.formattedOverall} of ${formattedTime}`;
      }

      return formattedTime;
    },
    showProgressBar() {
      return this.wantsInput && this.supportsInput;
    },
    progressBarTooltip() {
      let tooltip;

      if (this.isManualTime) {
        tooltip = 'Manual time';
      } else if (this.showProgressBar) {
        tooltip = `Overall: ${this.formattedOverall}<br/>Mouse: ${this.formattedMouse} Keyboard: ${this.formattedKeyboard}`;
      }

      return tooltip;
    },
    fakeDataRequired() {
      return this.fakeData.required;
    },
    shotBodyClass() {
      return this.fakeDataRequired ? 'no-actions no-screenshot' : '';
    },
    timeSlot() {
      return this.activity?.time_slot;
    },
    isSelected() {
      return this.selectedTimeSlots.includes(this.timeSlot);
    },
  },
  mounted() {
    this.observer?.observe(this.$el);
  },
  methods: {
    ...mapActions(['selectTimeSlots', 'deselectTimeSlots']),
    showGalleryOrSelect() {
      if (this.firstScreenshot) {
        this.$appGallery.openItemByIndex(this.firstScreenshot.index);
      } else if (!this.shouldShowScreenshots) {
        if (!this.canSelectActivities) {
          return;
        }

        if (this.isSelected) {
          this.deselectTimeSlots([this.timeSlot]);
        } else {
          this.selectTimeSlots([
            {
              timeSlot: this.timeSlot,
              screens: this.activity.screens,
              tracked: this.activity.tracked,
            },
          ]);
        }
      }
    },
    goToPlansPage() {
      window.open(this.activity.upgrade_plan_url, '_blank');
    },
  },
};
</script>
